import React, { useState, useEffect } from 'react';
import {
    Search,
    FilterList,
    Download,
    QrCode,
    Smartphone,
    CreditCard,
    CheckCircle,
    Cancel,
    Schedule,
    Refresh,
    Visibility
} from '@mui/icons-material';
import AdminSidebar from '../components/admin/AdminSidebar';
import DashboardHeader from '../components/admin/DashboardHeader';
import { useAuth } from '../contexts/AuthContext';
import { fetchTransactions } from '../services/transactionService';

const PaymentCard = ({ transaction, currency, onViewDetails }) => {
    const getMethodIcon = (method) => {
        const methodLower = method?.toLowerCase() || '';
        if (methodLower.includes('qr') || methodLower.includes('code')) {
            return <QrCode className="text-blue-500" />;
        } else if (methodLower.includes('mobile') || methodLower.includes('money')) {
            return <Smartphone className="text-green-500" />;
        } else if (methodLower.includes('card') || methodLower.includes('carte')) {
            return <CreditCard className="text-purple-500" />;
        }
        return <CreditCard className="text-gray-500" />;
    };

    const getStatusColor = (status) => {
        const statusLower = status?.toLowerCase() || '';
        if (statusLower === 'success' || statusLower === 'completed' || statusLower === 'paid') {
            return 'text-green-700 bg-green-50 border-green-200';
        } else if (statusLower === 'failed' || statusLower === 'rejected' || statusLower === 'cancelled') {
            return 'text-red-700 bg-red-50 border-red-200';
        } else if (statusLower === 'pending' || statusLower === 'processing') {
            return 'text-yellow-700 bg-yellow-50 border-yellow-200';
        }
        return 'text-gray-700 bg-gray-50 border-gray-200';
    };

    const getStatusText = (status) => {
        const statusLower = status?.toLowerCase() || '';
        if (statusLower === 'success' || statusLower === 'completed' || statusLower === 'paid') {
            return 'Réussi';
        } else if (statusLower === 'failed' || statusLower === 'rejected' || statusLower === 'cancelled') {
            return 'Échoué';
        } else if (statusLower === 'pending' || statusLower === 'processing') {
            return 'En attente';
        }
        return status || 'Inconnu';
    };

    const formatCurrency = (amount, curr) => {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: curr || 'CDF'
        }).format(amount || 0);
    };

    const formatDate = (date) => {
        if (!date) return 'N/A';
        const dateObj = typeof date === 'string' ? new Date(date) : date;
        return dateObj.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const formatTime = (date) => {
        if (!date) return 'N/A';
        const dateObj = typeof date === 'string' ? new Date(date) : date;
        return dateObj.toLocaleTimeString('fr-FR', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const transactionDate = transaction.created_at || transaction.createdAt || transaction.timestamp;
    const transactionCurrency = transaction.currency || currency || 'CDF';
    const transactionAmount = transaction.amount || 0;
    const transactionMethod = transaction.method || transaction.payment_method || transaction.provider?.name || 'N/A';
    const transactionStatus = transaction.status || 'unknown';
    const clientName = transaction.customer?.name || transaction.client?.name || transaction.payer?.name || 'Client';
    const caissierName = transaction.employee?.name || transaction.cashier?.name || transaction.caissier || 'N/A';

    return (
        <div className="bg-white rounded-2xl p-6 border border-slate-200 hover:shadow-lg transition-all duration-300">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                    {getMethodIcon(transactionMethod)}
                    <div>
                        <h3 className="font-bold text-slate-900">#{transaction.id || transaction.ref || 'N/A'}</h3>
                        <p className="text-sm text-slate-600">{clientName}</p>
                    </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(transactionStatus)}`}>
                    {getStatusText(transactionStatus)}
                </span>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                    <p className="text-sm text-slate-600">Montant</p>
                    <p className="font-semibold text-slate-900">
                        {formatCurrency(transactionAmount, transactionCurrency)}
                    </p>
                </div>
                <div>
                    <p className="text-sm text-slate-600">Caissier</p>
                    <p className="font-semibold text-slate-900">{caissierName}</p>
                </div>
            </div>

            <div className="flex items-center justify-between text-sm text-slate-600 mb-3">
                <span>{formatDate(transactionDate)}</span>
                <span>{formatTime(transactionDate)}</span>
            </div>

            {onViewDetails && (
                <button
                    onClick={() => onViewDetails(transaction)}
                    className="w-full mt-3 flex items-center justify-center space-x-2 px-4 py-2 bg-amber-50 text-amber-700 rounded-xl hover:bg-amber-100 transition-colors text-sm font-medium"
                >
                    <Visibility className="w-4 h-4" />
                    <span>Voir détails</span>
                </button>
            )}
        </div>
    );
};

const PaymentManagement = () => {
    const { selectedOrganization } = useAuth();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [timeRange, setTimeRange] = useState('today');
    const [currency, setCurrency] = useState('CDF');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        total: 0,
        success: 0,
        failed: 0,
        pending: 0,
        amount: { USD: 0, CDF: 0 }
    });
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [page, setPage] = useState(1);
    const [limit] = useState(20);

    useEffect(() => {
        if (selectedOrganization?.id) {
            loadTransactions();
        }
    }, [selectedOrganization, statusFilter, page]);

    const loadTransactions = async () => {
        if (!selectedOrganization?.id) return;
        
        try {
            setLoading(true);
            const solutionId = localStorage.getItem('netikash_solution');
            
            const response = await fetchTransactions({
                own: selectedOrganization.id,
                sol: solutionId,
                srv: 'all',
                act: 'all',
                ref: 'all',
                opr: 'all',
                crc: currency === 'all' ? 'all' : currency,
                sts: statusFilter === 'all' ? 'all' : statusFilter,
                lmt: limit,
                pge: page
            });

            const transactionsList = response.contents || [];
            setTransactions(transactionsList);

            // Calculer les statistiques
            const allTransactions = await fetchTransactions({
                own: selectedOrganization.id,
                sol: solutionId,
                srv: 'all',
                act: 'all',
                ref: 'all',
                opr: 'all',
                crc: 'all',
                sts: 'all',
                lmt: 1000,
                pge: 1
            });

            const allTxns = allTransactions.contents || [];
            const successTxns = allTxns.filter(t => 
                t.status?.toLowerCase() === 'success' || 
                t.status?.toLowerCase() === 'completed' || 
                t.status?.toLowerCase() === 'paid'
            );
            const failedTxns = allTxns.filter(t => 
                t.status?.toLowerCase() === 'failed' || 
                t.status?.toLowerCase() === 'rejected' || 
                t.status?.toLowerCase() === 'cancelled'
            );
            const pendingTxns = allTxns.filter(t => 
                t.status?.toLowerCase() === 'pending' || 
                t.status?.toLowerCase() === 'processing'
            );

            const totalAmountUSD = successTxns
                .filter(t => t.currency === 'USD')
                .reduce((sum, t) => sum + (t.amount || 0), 0);
            
            const totalAmountCDF = successTxns
                .filter(t => t.currency === 'CDF')
                .reduce((sum, t) => sum + (t.amount || 0), 0);

            setStats({
                total: allTxns.length,
                success: successTxns.length,
                failed: failedTxns.length,
                pending: pendingTxns.length,
                amount: { USD: totalAmountUSD, CDF: totalAmountCDF }
            });
        } catch (error) {
            console.error('Error loading transactions:', error);
            setTransactions([]);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount, curr) => {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: curr || currency
        }).format(amount || 0);
    };

    const filteredTransactions = transactions.filter(transaction => {
        const matchesSearch = !searchTerm || 
            (transaction.id && transaction.id.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (transaction.ref && transaction.ref.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (transaction.customer?.name && transaction.customer.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (transaction.client?.name && transaction.client.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (transaction.employee?.name && transaction.employee.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (transaction.cashier?.name && transaction.cashier.name.toLowerCase().includes(searchTerm.toLowerCase()));
        
        return matchesSearch;
    });

    return (
        <div className="min-h-screen bg-white flex">
            <AdminSidebar
                isOpen={sidebarOpen}
                onToggle={() => setSidebarOpen(!sidebarOpen)}
                activeSection="payment"
            />

            <div className="flex-1 min-h-screen transition-all duration-300 overflow-hidden">
                <div className="bg-gradient-to-br from-gray-50 via-white to-amber-50 min-h-screen p-4 lg:p-6">
                    <div className="relative z-10 space-y-6">
                        <DashboardHeader
                            timeRange={timeRange}
                            onTimeRangeChange={setTimeRange}
                            currency={currency}
                            onCurrencyChange={setCurrency}
                            onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
                        />

                        {/* Statistiques Rapides */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                            <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
                                <p className="text-sm text-slate-600 mb-2">Total Paiements</p>
                                <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                            </div>
                            <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
                                <p className="text-sm text-slate-600 mb-2">Réussis</p>
                                <p className="text-2xl font-bold text-green-600">{stats.success}</p>
                            </div>
                            <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
                                <p className="text-sm text-slate-600 mb-2">En Échec</p>
                                <p className="text-2xl font-bold text-red-600">{stats.failed}</p>
                            </div>
                            <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
                                <p className="text-sm text-slate-600 mb-2">Chiffre d'Affaires</p>
                                <p className="text-2xl font-bold text-amber-600">
                                    {formatCurrency(stats.amount[currency] || 0, currency)}
                                </p>
                            </div>
                        </div>

                        {/* Barre de Contrôle */}
                        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
                            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                                <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                                        <input
                                            type="text"
                                            placeholder="Rechercher un paiement..."
                                            className="pl-10 pr-4 py-3 border border-slate-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </div>
                                    <select
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                        className="px-4 py-3 border border-slate-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                    >
                                        <option value="all">Tous les statuts</option>
                                        <option value="success">Réussis</option>
                                        <option value="failed">Échoués</option>
                                        <option value="pending">En attente</option>
                                    </select>
                                    <button
                                        onClick={loadTransactions}
                                        disabled={loading}
                                        className="flex items-center space-x-2 px-4 py-3 border border-slate-300 rounded-2xl hover:bg-slate-50 transition-colors disabled:opacity-50"
                                    >
                                        <Refresh className={loading ? 'animate-spin' : ''} />
                                        <span>Actualiser</span>
                                    </button>
                                </div>

                                <div className="flex space-x-3">
                                    <button className="flex items-center space-x-2 px-4 py-3 bg-amber-500 text-white rounded-2xl hover:bg-amber-600 transition-colors shadow-sm">
                                        <Download />
                                        <span>Exporter</span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Liste des Paiements */}
                        {loading ? (
                            <div className="flex justify-center items-center py-12">
                                <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
                            </div>
                        ) : filteredTransactions.length === 0 ? (
                            <div className="bg-white rounded-2xl p-12 border border-slate-200 text-center">
                                <p className="text-slate-600 text-lg">Aucun paiement trouvé</p>
                                <p className="text-slate-400 text-sm mt-2">Essayez de modifier vos filtres de recherche</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                                {filteredTransactions.map(transaction => (
                                    <PaymentCard 
                                        key={transaction.id || transaction.ref || Math.random()} 
                                        transaction={transaction}
                                        currency={currency}
                                        onViewDetails={setSelectedTransaction}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PaymentManagement;